import { DateTime, Interval } from 'luxon';
import {
  Order,
  OrderPaymentMethod,
  OrderProduct,
  OrderStatus,
  OrderType,
} from '../../clients/quipli-service/order/order';
import { UserAddress } from '../../clients/quipli-service/user/user';
import { QuipliLink } from '../../components/link/quipli-link';
import { Typography } from '../../components/typography/typography';
import { Location } from '../../clients/quipli-service/location/location';
import './order-table-cells.scss';
import classNames from 'classnames';
import { ProductTrackingType } from '../../clients/quipli-service/product/product';
import { formatAddress, getOrderSourceIcon, getOrderStatusLabel, sumByLineItemType } from './order-utils';
import Icon from '../../components/icons/icon';
import { formatPhoneNumber } from '../../util/util';
import { isMobileOnly } from 'react-device-detect';
import { Tooltip } from '../../components/tooltip/tooltip';
import { PaymentMethodType } from '../../clients/quipli-service/payment/payment';
import _ from 'lodash';
import { StatusTransitionButtonsV2 } from './status-transitions/status-transition-button-v2';
import { InvoiceStatus } from '../../clients/quipli-service/invoice/invoice';
import { formatShippingType, ShippingType } from '../../clients/quipli-service/delivery/delivery';
import { Admin } from '../../clients/quipli-service/admin/admin';

type OrderTableCellProps = {
  order: Order;
  financialsV2?: boolean;
  locations?: Location[];
};

type AssignedUnitCellProps = OrderTableCellProps & {
  trackingVia: 'serialNumber' | 'unitNumber';
  isOrderList?: boolean;
};

type DateTimeCellProps = {
  date: DateTime | undefined;
  timeZone?: boolean;
  isEdit?: boolean;
};

type OrderActionCellProps = OrderTableCellProps & {
  locations?: Location[];
  reload: () => void;
};

type FormatAmountCellProps = {
  amount?: number;
  order: Order;
  locations?: Location[];
  elementType?: string;
};

type OrderAddressCellProps = {
  address?: UserAddress;
  shortened?: boolean;
};

export const RowNumCell = ({ order }: { order: Order }) => {
  return (
    <Typography
      type="text-sm"
      weight="regular"
      className="flex justify-center justify-items-center	gap-1 text-[var(--quipli-black-800)]"
    >
      {getOrderSourceIcon(order.source)}
    </Typography>
  );
};

export const OrderNumberCell = ({ order }: React.PropsWithChildren<OrderTableCellProps>) => (
  <Typography type="text-sm" weight="regular">
    <QuipliLink to={`orders/view/${order.id}`} className="underline underline-offset-1 text-[var(--quipli-black-800)]">
      {order.orderNumber || 'N/A'}
    </QuipliLink>
  </Typography>
);

export const ProductNameCell = ({ order }: React.PropsWithChildren<OrderTableCellProps>) => (
  <>
    {order.products.map((op) => (
      <Typography key={op.id} type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
        - {op.name}
      </Typography>
    ))}
  </>
);

export const AssignedUnitCell = ({
  order,
  trackingVia,
  locations = [],
  isOrderList = false,
}: React.PropsWithChildren<AssignedUnitCellProps>) => {
  const unitTrackingField = trackingVia === 'serialNumber' ? 'serialNumber' : 'unitNumber';
  const orderStatus = getOrderStatusLabel(order);
  const isOrderPendingOrApproved =
    isOrderList && (orderStatus === 'Reservation Request' || orderStatus === 'Reservation Approved');

  const getAssignedUnits = (op: OrderProduct) => {
    switch (op.product.trackingType) {
      case ProductTrackingType.SerialUnits:
        return op.assignedUnits.length
          ? op.assignedUnits.map((au) => {
              const locationName = locations.find((l) => l.id === au.locationId)?.name;
              const tooltipMessage = locationName
                ? `Unit ${au[unitTrackingField]} is currently assigned to ${locationName} location.Change location before moving forward.`
                : `Unit ${au[unitTrackingField]} is assigned to a different location than the order is taking place.`;
              const showIcon = isOrderPendingOrApproved && au.locationId !== order.locationId;
              return (
                <div key={au[unitTrackingField]} className="d-flex gap-1">
                  {showIcon && (
                    <Tooltip
                      tooltip={<span style={{ color: 'white', fontWeight: 400 }}>{tooltipMessage}</span>}
                      type="black"
                    >
                      <Icon icon="InfoCircle" size="medium" stroke="#dc3545" />
                    </Tooltip>
                  )}
                  <QuipliLink
                    to={`products/form/${op.product.id + '/productUnits'}`}
                    className="underline underline-offset-1 text-[var(--quipli-black-800)]"
                  >
                    <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
                      {au[unitTrackingField]}
                    </Typography>
                  </QuipliLink>
                </div>
              );
            })
          : null;
      case ProductTrackingType.Quantity:
        return (
          <Typography key={op.id} type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
            {op.quantity} unit(s)
          </Typography>
        );
    }
  };

  return (
    <div className="flex flex-col">
      {order.products.length
        ? order.products.flatMap((op, index) => (
            <div key={index}>
              <Typography key={op.name} type="text-sm" weight="regular">
                <QuipliLink
                  to={`products/form/${op.product.id}`}
                  className="underline underline-offset-1 text-[var(--quipli-black-800)]"
                >
                  {op.name}
                </QuipliLink>
              </Typography>
              {getAssignedUnits(op)}
            </div>
          ))
        : null}
    </div>
  );
};

export const PastDueCell = ({ order }: { order: Order }) => {
  let d = Interval.fromDateTimes(
    DateTime.min(...order.products.flatMap((op) => (op.rentalEndDate ? [op.rentalEndDate] : []))),
    DateTime.local(),
  ).toDuration(['days', 'hours'], { conversionAccuracy: 'longterm' });
  d = d.set({ hours: Math.round(d.hours) });

  return (
    <Typography type="text-sm" weight="regular" className="text-[red]">
      {d.toHuman()}
    </Typography>
  );
};

export const CancelledReasonCell = ({ order }: React.PropsWithChildren<OrderTableCellProps>) => (
  <Typography type="text-sm" weight="regular" className="text-[red]">
    {order?.cancellationReason}
    {order?.cancellationMessage ? ` (${order.cancellationMessage})` : ''}
  </Typography>
);

export const CustomerNameCell = ({ order }: React.PropsWithChildren<OrderTableCellProps>) => (
  <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
    <QuipliLink
      to={`users/customer_form/${order.user.id}`}
      className="underline underline-offset-1 text-[var(--quipli-black-800)]"
    >
      {order.user.name}
    </QuipliLink>
  </Typography>
);

export const CustomerPhoneNumberCell = ({ order }: React.PropsWithChildren<OrderTableCellProps>) => (
  <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
    <a href={`tel:${order.user.phone}`} className="text-[var(--quipli-black-800)] flex gap-3">
      {formatPhoneNumber(order.user.phone)}
      {isMobileOnly && <Icon icon="Phone" stroke="#12B76A" />}
    </a>
  </Typography>
);

export const DateTimeCell = ({ date, timeZone, isEdit = false }: React.PropsWithChildren<DateTimeCellProps>) => (
  <div>
    <Typography
      type="text-sm"
      weight="regular"
      className={classNames('text-[var(--quipli-black-800)]', { 'text-[var(--link-200)]': isEdit })}
    >
      {date && date.toFormat('LLL-dd')}
    </Typography>
    <Typography
      type="text-sm"
      weight="regular"
      className={classNames('text-[var(--grey-500)]', { 'text-[var(--link-200)]': isEdit })}
    >
      {date && date.toFormat(timeZone ? 'ttt' : 't')}
    </Typography>
  </div>
);

export const LocationCell = ({ order, locations = [] }: React.PropsWithChildren<OrderTableCellProps>) => {
  return (
    <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
      {locations.find((l) => l.id === order.locationId)?.name}
    </Typography>
  );
};

export const DeliveryCell = ({ order }: React.PropsWithChildren<OrderTableCellProps>) => {
  const shippingTypes = order.products
    .map((op) => op.shippingType)
    .filter((v, i, a) => a.findIndex((o) => o === v) === i);

  return (
    <div className="flex flex-col">
      {shippingTypes.map((st) => (
        <Typography key={st} type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
          <span
            key={st}
            title={st !== ShippingType.SelfPickUp && order.shippingAddress ? formatAddress(order.shippingAddress) : ''}
          >
            {formatShippingType(st)}
          </span>
        </Typography>
      ))}
    </div>
  );
};

export const RentalStartCell = ({ order }: React.PropsWithChildren<OrderTableCellProps>) => {
  const rentals = order.products.filter((op) => op.type === OrderType.Rental);
  const rentalStartDate =
    rentals.length && DateTime.min(...rentals.map((op) => op.actualRentalStartDate || op.rentalStartDate));
  return rentalStartDate ? (
    <DateTimeCell date={rentalStartDate} />
  ) : (
    <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
      N/A
    </Typography>
  );
};

export const RentalEndCell = ({ order }: React.PropsWithChildren<OrderTableCellProps>) => {
  const rentals = order.products.filter((op) => op.type === OrderType.Rental);
  const rentalEndDate =
    rentals.length &&
    DateTime.max(...rentals.flatMap((op) => (op.rentalEndDate ? [op.actualRentalEndDate || op.rentalEndDate] : [])));
  return !rentals.some((op) => !op.rentalEndDate) && rentalEndDate ? (
    <DateTimeCell date={rentalEndDate} />
  ) : (
    <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
      N/A
    </Typography>
  );
};

export const AgreementTimeCell = ({ order }: React.PropsWithChildren<OrderTableCellProps>) => {
  return order.signatureDate ? <DateTimeCell date={order.signatureDate} timeZone={true} /> : null;
};

export const FormatAmountCell = ({
  amount = 0,
  order,
  locations = [],
  elementType = 'p',
}: React.PropsWithChildren<FormatAmountCellProps>) => {
  const fmt = Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: locations.find((l) => l.id === order.id)?.currency || 'USD',
  });

  const formattedAmount = fmt.format(amount);

  return (
    <>
      {elementType === 'p' ? (
        <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
          {formattedAmount}
        </Typography>
      ) : (
        <span>{formattedAmount}</span>
      )}
    </>
  );
};

export function getClassForLabel(label: string): string {
  switch (label) {
    case 'Quote':
      return 'quote';
    case 'Reservation Request':
      return 'reservation-request';
    case 'Cancelled':
      return 'cancelled';
    case 'Closed':
    case 'Approved':
    case 'On Rent':
    case 'Reservation Approved':
      return 'closed-approved';
    case 'Rental Ended':
      return 'rental-end-closed';
    default:
      return 'default';
  }
}

export const OrderStatusCell = ({ order }: React.PropsWithChildren<OrderTableCellProps>) => {
  const label = getOrderStatusLabel(order);

  return (
    <div className={classNames('w-fit px-[12px] py-[5px] rounded-2xl order-status-cell', getClassForLabel(label))}>
      <Typography type="text-sm" weight="regular">
        {label}
      </Typography>
    </div>
  );
};

export const PaymentMethodCell = ({ order }: React.PropsWithChildren<OrderTableCellProps>) => {
  const getPaymentMethodLabel = (order: Order) => {
    if (order.status === OrderStatus.Quote) {
      return (
        <Typography type="text-sm" weight="regular">
          Pending
        </Typography>
      );
    } else if (order.paymentMethod.type === PaymentMethodType.OnAccount) {
      return (
        <Typography type="text-sm" weight="regular">
          On Account
          <Typography type="text-sm" weight="semi-bold">
            {order.paymentMethod.account.accountNumber}
          </Typography>
        </Typography>
      );
    } else if (order.paymentMethodType === OrderPaymentMethod.ProceedWithoutPayment) {
      return (
        <Typography type="text-sm" weight="regular">
          None
        </Typography>
      );
    } else if (order.paymentMethodType === OrderPaymentMethod.Cash) {
      return (
        <Typography type="text-sm" weight="regular">
          Cash
        </Typography>
      );
    } else if (order.paymentMethodType === OrderPaymentMethod.Check) {
      return (
        <Typography type="text-sm" weight="regular">
          Check
        </Typography>
      );
    } else if (order.paymentMethodType === OrderPaymentMethod.BankAccount) {
      return (
        <Typography type="text-sm" weight="regular">
          Bank Account
        </Typography>
      );
    } else if (order.paymentMethodType === OrderPaymentMethod.Custom) {
      return (
        <Typography type="text-sm" weight="regular">
          {order.paymentType}
        </Typography>
      );
    } else {
      // Credit Card

      const paymenyType = order.paymentType?.toLowerCase();
      let icon = null;
      switch (paymenyType) {
        case 'visa':
          icon = <Icon icon="VisaCardLogo" size="xlarge" />;
          break;
        case 'mastercard':
          icon = <Icon icon="MasterCardLogo" size="xlarge" />;
          break;
        case 'afterpay':
          icon = <Icon icon="AfterPayLogo" size="xlarge" />;
          break;
        case 'affirm':
          icon = undefined;
          break;
      }

      return (
        <>
          {icon}
          {paymenyType?.toLowerCase() !== 'affirm' ? (
            <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
              **** {order.paymentLastFourDigits}
            </Typography>
          ) : (
            'Affirm'
          )}
        </>
      );
    }
  };

  return getPaymentMethodLabel(order);
};

export const ActionCell = ({ order, locations, reload }: React.PropsWithChildren<OrderActionCellProps>) => {
  const location = locations?.find((l) => l.id === order.locationId);
  return (
    <>
      {location && (
        <StatusTransitionButtonsV2
          order={order}
          location={location}
          isDisabled={false}
          reload={reload}
          hideTransitions={['reject', 'reopen', 'missed']}
        />
      )}
    </>
  );
};

export const ProductFeesCell = ({ order }: React.PropsWithChildren<OrderTableCellProps>) => {
  const allProductFees = order.products.flatMap((op) => op.additionalFees);
  return (
    <>
      {allProductFees.length > 0 ? (
        allProductFees.map((fee, index) => (
          <span key={index}>
            <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
              {fee.feeTitle}:
              <FormatAmountCell amount={fee.feeAppliedValue} order={order} elementType="span" />
            </Typography>
          </span>
        ))
      ) : (
        <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
          N/A
        </Typography>
      )}
    </>
  );
};

export const ProductFeesTotalCell = ({ order }: React.PropsWithChildren<OrderTableCellProps>) => {
  const totalFeeValue = sumByLineItemType(order.invoices, 'ProductFee');

  return (
    <>
      <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
        <FormatAmountCell amount={totalFeeValue} order={order} elementType="span" />
      </Typography>
    </>
  );
};

export const OrderFeesCell = ({ order }: React.PropsWithChildren<OrderTableCellProps>) => {
  return (
    <>
      {order.additionalFees.length > 0 ? (
        order.additionalFees.map((fee, index) => (
          <span key={index}>
            <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
              {fee.feeTitle}:
              <FormatAmountCell amount={fee.feeAppliedValue} order={order} elementType="span" />
            </Typography>
          </span>
        ))
      ) : (
        <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
          N/A
        </Typography>
      )}
    </>
  );
};

export const OrderFeesTotalCell = ({ order }: React.PropsWithChildren<OrderTableCellProps>) => {
  const totalFeeValue = sumByLineItemType(order.invoices, 'OrderFee');
  return (
    <>
      <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
        <FormatAmountCell amount={totalFeeValue} order={order} elementType="span" />
      </Typography>
    </>
  );
};

export const TotalTaxAmountCell = ({ order }: React.PropsWithChildren<OrderTableCellProps>) => {
  const amount = _.sum(order.invoices.map((inv) => inv.tax));
  return (
    <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
      <FormatAmountCell amount={amount} order={order} elementType="span" />
    </Typography>
  );
};

export const UpchargeReasonsCell = ({ order }: React.PropsWithChildren<OrderTableCellProps>) => {
  const allUpchargeReasons = order.invoices
    .flatMap((inv) => inv.lineItems)
    .filter(
      (li) =>
        li.total > 0 &&
        ((li.type === 'OrderPayment' && li.name.toLowerCase().indexOf('Security Deposit') < 0) ||
          li.type === 'OrderRequest'),
    )
    .map((li) => li.name.replace('Upcharge:', '').trim())
    .join(',');
  return (
    <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
      {allUpchargeReasons ? allUpchargeReasons : 'N/A'}
    </Typography>
  );
};
export const TotalUpchargeAmountCell = ({ order }: React.PropsWithChildren<OrderTableCellProps>) => {
  const totalUpcharges = _.sum(
    order.invoices
      .flatMap((inv) => inv.lineItems)
      .filter(
        (li) =>
          li.total > 0 &&
          ((li.type === 'OrderPayment' && li.name.toLowerCase().indexOf('Security Deposit') < 0) ||
            li.type === 'OrderRequest'),
      )
      .map((li) => li.total),
  );

  return (
    <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
      <FormatAmountCell amount={totalUpcharges} order={order} elementType="span" />
    </Typography>
  );
};
export const RefundReasonsCell = ({ order }: React.PropsWithChildren<OrderTableCellProps>) => {
  const allRefundReasons = order.invoices
    .flatMap((inv) => inv.lineItems)
    .filter((li) => li.type === 'OrderPayment' && li.total < 0 && li.name.startsWith('Refund'))
    .map((li) => li.name.replace('Refund:', '').trim())
    .join(',');
  return (
    <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
      {allRefundReasons ? allRefundReasons : 'N/A'}
    </Typography>
  );
};
export const TotalRefundAmountCell = ({ order }: React.PropsWithChildren<OrderTableCellProps>) => {
  const totalRefunds = _.sum(
    order.invoices
      .flatMap((inv) => inv.lineItems)
      .filter((li) => li.type === 'OrderPayment' && li.total < 0)
      .map((li) => li.total),
  );

  return (
    <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
      <FormatAmountCell amount={totalRefunds} order={order} elementType="span" />
    </Typography>
  );
};

export const AddressCell = ({ address, shortened = false }: React.PropsWithChildren<OrderAddressCellProps>) => {
  let address1 = '';
  let remainingPart = '';
  if (address) {
    const cityIndex = address.address1.toLowerCase().indexOf(address.city.toLowerCase());

    if (address.isGoogleGenerated && cityIndex !== -1) {
      address1 = address.address1.substring(0, cityIndex).trim();
      remainingPart = address.address1.substring(cityIndex).trim();
    } else {
      address1 = address.address1;
    }

    if (shortened) {
      if (address1.length > 0 && address1.at(address1.length - 1) == ',') {
        address1 = address1.substring(0, address1.length - 1);
      }
    }
  }

  const href = `https://www.google.com/maps/dir/?api=1&travelmode=driving&layer=traffic&destination=${address1} ${remainingPart}`;
  return address ? (
    <a href={href} target="_blank">
      <Typography type="text-sm">
        {address1}
        <br />
        {!shortened && <span>{remainingPart}</span>}
      </Typography>
    </a>
  ) : (
    <span>N/A</span>
  );
};

export const TotalAmountCell = ({ order }: React.PropsWithChildren<OrderTableCellProps>) => {
  const fmt = Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
  });

  const totalAmount = fmt.format(
    _.sum(
      order.invoices.map((inv) =>
        inv.status === 'Void'
          ? _.sum(
              inv.payments
                .filter((p) => p.status === 'succeeded')
                .flatMap((p) => p.invoicePayments)
                .map((p) => p.amount),
            )
          : inv.total,
      ),
    ),
  );

  return (
    <Typography type="text-sm" weight="bold" className="text-[var(--quipli-black-800)]">
      {totalAmount}
    </Typography>
  );
};

export const AmountCell = ({ order, locations = [] }: React.PropsWithChildren<OrderTableCellProps>) => {
  const fmt = Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: locations.find((l) => l.id === order.locationId)?.currency || 'USD',
  });

  const total = _.sum(order.invoices.filter((inv) => inv.status !== InvoiceStatus.Void).map((inv) => inv.total));
  return (
    <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
      {fmt.format(total)}
    </Typography>
  );
};

export const CreatedByCell = ({ admin }: { admin?: Admin }) => {
  return admin ? (
    <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
      {admin.adminFullName}
    </Typography>
  ) : null;
};
